import React, { useState } from 'react';
import { useDrop } from 'react-dnd';
import { MoreVertical, Clock, MessageSquare, Calendar, CheckSquare } from 'lucide-react';
import CandidateCard from './CandidateCard';

/**
 * PipelineStage - Enhanced stage column with modern HR styling
 * Features: Drag & drop, selection, stage actions, candidate count
 */
const PipelineStage = ({
  stage,
  candidates = [],
  onCandidateMove,
  onCandidateClick,
  onScheduleInterview,
  onAddNote,
  selectedCandidates = [],
  onCandidateSelect
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showStageActions, setShowStageActions] = useState(false);

  // Drop configuration for drag & drop
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: 'candidate',
    drop: (item) => {
      if (item?.id && item?.currentStage !== stage?.id) {
        onCandidateMove?.(item?.id, stage?.id);
      }
    },
    collect: (monitor) => ({
      isOver: monitor?.isOver(),
      canDrop: monitor?.canDrop()
    })
  }), [stage?.id, onCandidateMove]);

  const candidateCount = candidates?.length || 0;
  const selectedCount = candidates?.filter(c => selectedCandidates?.includes(c?.id))?.length || 0;

  // Stage color mapping for visual distinction
  const stageColors = {
    'new': 'from-blue-500 to-blue-600',
    'screening': 'from-amber-500 to-amber-600', 
    'interview': 'from-purple-500 to-purple-600',
    'final-review': 'from-indigo-500 to-indigo-600',
    'offer': 'from-emerald-500 to-emerald-600',
    'hired': 'from-green-600 to-green-700'
  };

  const stageBorderColors = {
    'new': 'border-blue-200',
    'screening': 'border-amber-200',
    'interview': 'border-purple-200', 
    'final-review': 'border-indigo-200',
    'offer': 'border-emerald-200',
    'hired': 'border-green-200'
  };

  const stageBackgrounds = {
    'new': 'bg-blue-50/60',
    'screening': 'bg-amber-50/60',
    'interview': 'bg-purple-50/60',
    'final-review': 'bg-indigo-50/60', 
    'offer': 'bg-emerald-50/60',
    'hired': 'bg-green-50/60'
  };

  const gradientClass = stageColors?.[stage?.id] || 'from-gray-500 to-gray-600';
  const borderClass = stageBorderColors?.[stage?.id] || 'border-gray-200';
  const backgroundClass = stageBackgrounds?.[stage?.id] || 'bg-gray-50/60';

  const handleSelectAll = () => {
    const allIds = candidates?.map(c => c?.id) || [];
    const allSelected = allIds?.every(id => selectedCandidates?.includes(id));
    
    allIds?.forEach(id => {
      onCandidateSelect?.(id, !allSelected);
    });
  };

  return (
    <div
      ref={drop}
      className={`flex-shrink-0 w-80 ${backgroundClass} backdrop-blur-sm border ${borderClass} rounded-2xl transition-all duration-200 ${
        isOver && canDrop 
          ? 'ring-2 ring-violet-400 ring-opacity-50 shadow-lg transform scale-[1.02]' 
          : 'shadow-md hover:shadow-lg'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setShowStageActions(false);
      }}
    >
      {/* Stage Header */}
      <div className="relative">
        <div className={`bg-gradient-to-r ${gradientClass} rounded-t-2xl p-4 text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="font-semibold text-lg">{stage?.name}</h3>
              <div className="bg-white/20 backdrop-blur-sm rounded-full px-3 py-1">
                <span className="text-sm font-medium">{candidateCount}</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {candidateCount > 0 && (
                <button
                  onClick={handleSelectAll}
                  className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                  title={selectedCount > 0 ? "Deselect All" : "Select All"}
                >
                  <CheckSquare className={`w-4 h-4 ${selectedCount > 0 ? 'fill-current' : ''}`} />
                </button>
              )}
              
              <button
                onClick={() => setShowStageActions(!showStageActions)}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              >
                <MoreVertical className="w-4 h-4" />
              </button>
            </div>
          </div>

          {selectedCount > 0 && (
            <div className="mt-2 text-sm bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1 w-fit">
              {selectedCount} selected
            </div>
          )}
        </div>

        {/* Stage Actions Dropdown */}
        {showStageActions && (
          <div className="absolute top-full right-4 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-10 min-w-48">
            <div className="py-2">
              <button
                onClick={() => console.log('Add candidate to stage')}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <Clock className="w-4 h-4" />
                Add Candidate
              </button>
              <button
                onClick={() => console.log('Bulk schedule interviews')}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                disabled={candidateCount === 0}
              >
                <Calendar className="w-4 h-4" />
                Bulk Schedule Interviews
              </button>
              <button
                onClick={() => console.log('Add stage note')}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <MessageSquare className="w-4 h-4" />
                Add Stage Note
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Candidates List */}
      <div className="p-4 space-y-3 max-h-[600px] overflow-y-auto custom-scrollbar">
        {candidates?.length > 0 ? (
          candidates?.map((candidate, index) => (
            <CandidateCard
              key={candidate?.id}
              candidate={candidate}
              onClick={() => onCandidateClick?.(candidate)}
              onScheduleInterview={() => onScheduleInterview?.(candidate)}
              onAddNote={() => onAddNote?.(candidate)}
              isSelected={selectedCandidates?.includes(candidate?.id)}
              onSelect={(selected) => onCandidateSelect?.(candidate?.id, selected)}
              stage={stage?.id}
              index={index}
            />
          ))
        ) : (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-white/60 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <span className="text-xl opacity-60">📋</span>
            </div>
            <p className="text-sm text-gray-600 font-medium mb-1">No candidates</p>
            <p className="text-xs text-gray-500">
              {isOver && canDrop ? 'Drop candidates here' : 'Candidates will appear here'}
            </p>
          </div>
        )}
      </div>
      {/* Drop Zone Visual Indicator */}
      {isOver && canDrop && (
        <div className="absolute inset-0 bg-violet-400/10 border-2 border-dashed border-violet-400 rounded-2xl pointer-events-none flex items-center justify-center">
          <div className="bg-violet-500 text-white px-4 py-2 rounded-xl font-medium shadow-lg">
            Drop to move to {stage?.name}
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineStage;